import { useNotificationPush, useNotificationSummaryQuery } from '@multiplatform-apps/service-trips';
import { useCallback, useEffect, useRef, useState } from 'react';

import { usePopoverDismiss } from './use-popover-dismiss';

interface NotificationBellState {
  isOpen: boolean;
  shakeKey: number;
  totalUnread: number;
  categories: ReturnType<typeof useNotificationSummaryQuery>['data'] extends
    | { categories: infer C }
    | undefined
    ? C
    : never;
  hasNew: boolean;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  popoverRef: React.RefObject<HTMLDivElement | null>;
  handleToggle: () => void;
  handleClose: () => void;
}

export const useNotificationBell = (): NotificationBellState => {
  const { data: summary } = useNotificationSummaryQuery();
  const { hasNew, pushSignal } = useNotificationPush();
  const [isOpen, setIsOpen] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const totalUnread = summary?.totalUnread ?? 0;
  const categories = summary?.categories ?? [];

  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Re-trigger shake animation on each new push notification
  useEffect(() => {
    if (pushSignal > 0) {
      setShakeKey((k) => k + 1);
    }
  }, [pushSignal]);

  usePopoverDismiss({ isOpen, wrapperRef, buttonRef, onClose: handleClose });

  // Move focus into popover when opened
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        const firstFocusable = popoverRef.current?.querySelector<HTMLElement>(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
      });
    }
  }, [isOpen]);

  return {
    isOpen,
    shakeKey,
    totalUnread,
    categories,
    hasNew,
    wrapperRef,
    buttonRef,
    popoverRef,
    handleToggle,
    handleClose,
  };
};
